import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  CreateExternalNotePayload,
  DraftNoteContentRequest,
  DraftNoteContentResponse,
  ExternalCMSType,
  ExternalNoteActionSchema,
  ExternalNoteResolutionSchema,
  ExternalNoteSourceType,
} from '../types/external-note-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const externalNoteApi = createApi({
  reducerPath: 'externalNoteApi',
  tagTypes: ['ExternalNoteResolution', 'SourceActions'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    resolveExternalNote: builder.query<
      ExternalNoteResolutionSchema,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/external-notes/resolve/`,
      providesTags: (_result, _error, { matterId }) => [
        { type: 'ExternalNoteResolution', id: matterId },
      ],
    }),

    draftNoteContent: builder.mutation<
      DraftNoteContentResponse,
      { matterId: string; body: DraftNoteContentRequest }
    >({
      query: ({ matterId, body }) => ({
        url: `api/matters/${matterId}/external-notes/draft-content/`,
        method: 'POST',
        body,
      }),
    }),

    createExternalNote: builder.mutation<
      ExternalNoteActionSchema,
      {
        matterId: string;
        cmsType: ExternalCMSType;
        body: CreateExternalNotePayload;
      }
    >({
      query: ({ matterId, cmsType, body }) => ({
        url: `api/matters/${matterId}/external-notes/${cmsType}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { matterId }) => [
        { type: 'SourceActions', id: matterId },
      ],
    }),

    retryExternalNoteAction: builder.mutation<
      ExternalNoteActionSchema,
      {
        matterId: string;
        cmsType: ExternalCMSType;
        actionId: string;
      }
    >({
      query: ({ matterId, cmsType, actionId }) => ({
        url: `api/matters/${matterId}/external-notes/${cmsType}/actions/${actionId}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { matterId }) => [
        { type: 'SourceActions', id: matterId },
      ],
    }),

    getSourceActions: builder.query<
      Record<string, ExternalNoteActionSchema>,
      {
        matterId: string;
        sourceType: ExternalNoteSourceType;
        sourceIds: string[];
      }
    >({
      query: ({ matterId, sourceType, sourceIds }) =>
        `api/matters/${matterId}/external-notes/source-actions/?source_type=${sourceType}&source_ids=${sourceIds.join(',')}`,
      providesTags: (_result, _error, { matterId }) => [
        { type: 'SourceActions', id: matterId },
      ],
    }),
  }),
});

export const {
  useResolveExternalNoteQuery,
  useDraftNoteContentMutation,
  useCreateExternalNoteMutation,
  useRetryExternalNoteActionMutation,
  useGetSourceActionsQuery,
} = externalNoteApi;
